import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatCurrency';

const FinanceDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get('/api/reports/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const financialStats = [
    {
      name: 'Total Revenue',
      value: formatCurrency(dashboardData?.summary?.totalSales),
      icon: CurrencyDollarIcon,
      change: '+12%',
      changeType: 'positive',
      color: 'text-green-600'
    },
    {
      name: 'Total Expenses',
      value: formatCurrency(dashboardData?.summary?.totalExpenses),
      icon: ChartBarIcon,
      change: '+8%',
      changeType: 'negative',
      color: 'text-red-600'
    },
    {
      name: 'Net Profit',
      value: formatCurrency(dashboardData?.summary?.netProfit),
      icon: ArrowUpIcon,
      change: '+15%',
      changeType: 'positive',
      color: 'text-blue-600'
    },
    {
      name: 'Outstanding Debt',
      value: formatCurrency(dashboardData?.summary?.totalDebt),
      icon: ExclamationTriangleIcon,
      change: '-5%',
      changeType: 'positive',
      color: 'text-yellow-600'
    }
  ];

  const quickActions = [
    {
      name: 'Record Expense',
      href: '/finance/expenses',
      icon: DocumentTextIcon,
      description: 'Log business expenses',
      color: 'bg-red-500'
    },
    {
      name: 'View Transactions',
      href: '/finance/transactions',
      icon: CurrencyDollarIcon,
      description: 'Review all transactions',
      color: 'bg-blue-500'
    },
    {
      name: 'Manage Debts',
      href: '/finance/debts',
      icon: ExclamationTriangleIcon,
      description: 'Track outstanding debts',
      color: 'bg-yellow-500'
    },
    {
      name: 'Generate Reports',
      href: '/finance/reports',
      icon: ChartBarIcon,
      description: 'Create financial reports',
      color: 'bg-green-500'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-secondary-200 bg-white px-6 py-4">
        <h1 className="text-2xl font-bold text-secondary-900">Finance Dashboard</h1>
        <p className="mt-1 text-sm text-secondary-600">
          Monitor financial performance and manage transactions.
        </p>
      </div>

      {/* Financial Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {financialStats.map((stat) => (
          <div key={stat.name} className="card bg-secondary-50">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <stat.icon className={`h-8 w-8 ${stat.color}`} />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-secondary-600 truncate">
                      {stat.name}
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-secondary-900">
                        {stat.value}
                      </div>
                      <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                        stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {stat.changeType === 'positive' ? (
                          <ArrowUpIcon className="self-center flex-shrink-0 h-4 w-4" />
                        ) : (
                          <ArrowDownIcon className="self-center flex-shrink-0 h-4 w-4" />
                        )}
                        <span className="sr-only">
                          {stat.changeType === 'positive' ? 'Increased' : 'Decreased'} by
                        </span>
                        {stat.change}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-medium text-secondary-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {quickActions.map((action) => (
            <Link
              key={action.name}
              to={action.href}
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-lg border border-secondary-200 hover:border-secondary-300 transition-colors"
            >
              <div>
                <span className={`rounded-lg inline-flex p-3 ${action.color} text-white`}>
                  <action.icon className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium text-secondary-900">
                  {action.name}
                </h3>
                <p className="mt-2 text-sm text-secondary-500">
                  {action.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Financial Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card bg-secondary-50">
          <div className="card-header">
            <h3 className="text-lg font-medium text-secondary-900">Profit & Loss</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm text-secondary-600">Gross Revenue</span>
                <span className="text-sm font-medium text-secondary-900">
                  {formatCurrency(dashboardData?.summary?.totalSales)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-secondary-600">Total Expenses</span>
                <span className="text-sm font-medium text-secondary-900">
                  {formatCurrency(dashboardData?.summary?.totalExpenses)}
                </span>
              </div>
              <div className="border-t pt-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-secondary-900">Net Profit</span>
                  <span className={`text-sm font-medium ${
                    (dashboardData?.summary?.netProfit || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(dashboardData?.summary?.netProfit)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-secondary-50">
          <div className="card-header">
            <h3 className="text-lg font-medium text-secondary-900">Recent Transactions</h3>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary-900">Sale to Shop ABC</p>
                  <p className="text-xs text-secondary-500">2 hours ago</p>
                </div>
                <span className="text-sm font-medium text-green-600">
                  {formatCurrency(150, { withSign: true })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary-900">Fuel Expense</p>
                  <p className="text-xs text-secondary-500">4 hours ago</p>
                </div>
                <span className="text-sm font-medium text-red-600">
                  {formatCurrency(-50, { withSign: true })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary-900">Payment Received</p>
                  <p className="text-xs text-secondary-500">6 hours ago</p>
                </div>
                <span className="text-sm font-medium text-green-600">
                  {formatCurrency(200, { withSign: true })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Debt Summary */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-secondary-900">Debt Summary</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-secondary-900">
                {formatCurrency(dashboardData?.summary?.totalDebt)}
              </div>
              <div className="text-sm text-secondary-500">Total Outstanding</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(
                  (dashboardData?.summary?.totalSales || 0) -
                    (dashboardData?.summary?.totalDebt || 0)
                )}
              </div>
              <div className="text-sm text-secondary-500">Collected</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {dashboardData?.summary?.totalSales > 0 
                  ? (((dashboardData.summary.totalSales - dashboardData.summary.totalDebt) / dashboardData.summary.totalSales) * 100).toFixed(1)
                  : 0}%
              </div>
              <div className="text-sm text-secondary-500">Collection Rate</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanceDashboard;
